import { BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSubscription } from "@/hooks/useSubscription";
import { cn } from "@/lib/utils";

function getUsageTone(percentage: number) {
  if (percentage >= 80) {
    return {
      barClassName: "[&>div]:bg-destructive",
      textClassName: "text-destructive",
    };
  }

  if (percentage >= 60) {
    return {
      barClassName: "[&>div]:bg-[#C4A45A]",
      textClassName: "text-[#C4A45A]",
    };
  }

  return {
    barClassName: "[&>div]:bg-primary",
    textClassName: "text-primary",
  };
}

export function PlanUsageNavItem() {
  const navigate = useNavigate();
  const subscriptionState = useSubscription() as ReturnType<typeof useSubscription> & {
    planDisplayName?: string;
    planTier?: string;
    usageSummary?: {
      clients?: { used?: number; limit?: number; percentage?: number };
      members?: { used?: number; limit?: number; percentage?: number };
      posts?: { used?: number; limit?: number; percentage?: number };
      storage?: { used?: number; limit?: number; percentage?: number };
    };
  };
  const {
    planDisplayName,
    planTier,
    usageSummary,
    usage,
    plan,
    isLoading,
  } = subscriptionState;

  const safeUsageSummary = usageSummary || {};
  const clientsUsage = safeUsageSummary?.clients || {
    used: usage?.clients_used || 0,
    limit: usage?.clients_limit || 0,
    percentage: usage?.clients_limit ? Math.min(((usage?.clients_used || 0) / usage.clients_limit) * 100, 100) : 0,
  };
  const membersUsage = safeUsageSummary?.members || {
    used: usage?.members_used || 0,
    limit: usage?.members_limit || 0,
    percentage: usage?.members_limit ? Math.min(((usage?.members_used || 0) / usage.members_limit) * 100, 100) : 0,
  };
  const postsUsage = safeUsageSummary?.posts || {
    used: usage?.posts_used || 0,
    limit: usage?.posts_limit || 0,
    percentage: usage?.posts_limit ? Math.min(((usage?.posts_used || 0) / usage.posts_limit) * 100, 100) : 0,
  };
  const storageLimitGb = usage?.storage_limit_gb || 0;
  const storageUsedGb = usage?.storage_used_bytes ? usage.storage_used_bytes / 1024 / 1024 / 1024 : 0;
  const storageUsage = safeUsageSummary?.storage || {
    used: storageUsedGb || 0,
    limit: storageLimitGb || 0,
    percentage: storageLimitGb ? Math.min(((storageUsedGb || 0) / storageLimitGb) * 100, 100) : 0,
  };

  const items = [
    {
      label: "Clientes",
      used: clientsUsage?.used || 0,
      limit: clientsUsage?.limit || 0,
    },
    {
      label: "Membros",
      used: membersUsage?.used || 0,
      limit: membersUsage?.limit || 0,
    },
    {
      label: "Posts",
      used: postsUsage?.used || 0,
      limit: postsUsage?.limit || 0,
    },
    {
      label: "Armazenamento",
      used: storageUsage?.used || 0,
      limit: storageUsage?.limit || 0,
      format: (value: number) => `${value.toFixed(2)} GB`,
    },
  ];

  const maxUsagePercentage = Math.max(
    clientsUsage?.percentage || 0,
    membersUsage?.percentage || 0,
    postsUsage?.percentage || 0,
    storageUsage?.percentage || 0,
  );
  const tone = getUsageTone(storageUsage?.percentage || 0);
  const label = isLoading ? "Carregando plano..." : `Plano ${planDisplayName || plan?.name || "Free"}`;
  const tierLabel = (planTier || plan?.name || "free").toUpperCase();
  const hasUsageData = !!usageSummary || !!usage;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex w-full items-center gap-3 rounded-xl border border-sidebar-border/70 bg-sidebar-accent/40 px-3 py-3 text-left transition-colors hover:bg-sidebar-accent"
          type="button"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/12 text-primary">
            <BarChart3 className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-sidebar-foreground">{label}</p>
            <p className={cn("text-xs", tone.textClassName)}>
              {isLoading || !hasUsageData ? "Carregando uso..." : `${maxUsagePercentage}% do plano`}
            </p>
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent side="top" align="start" className="w-80 space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">Plano atual</p>
            <h4 className="text-base font-semibold text-foreground">
              {planDisplayName || plan?.name || "Free"}
            </h4>
          </div>
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            {tierLabel}
          </Badge>
        </div>

        {isLoading && !hasUsageData ? (
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
            Carregando limites e uso do plano...
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => {
              const percentage = item.limit > 0 ? Math.min((item.used / item.limit) * 100, 100) : 0;
              const usageTone = getUsageTone(percentage);
              const formatValue = item.format ?? ((value: number) => String(value));

              return (
                <div key={item.label} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className={cn("font-medium", usageTone.textClassName)}>
                      {formatValue(item.used || 0)} / {formatValue(item.limit || 0)}
                    </span>
                  </div>
                  <Progress value={percentage || 0} className={cn("h-2 bg-muted", usageTone.barClassName)} />
                </div>
              );
            })}
          </div>
        )}

        <Button
          className="w-full"
          variant="outline"
          onClick={() => navigate("/configuracoes?tab=billing")}
        >
          Ver plano completo
        </Button>
      </PopoverContent>
    </Popover>
  );
}
